import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { ImageUploader } from '../components/ImageUploader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { User, Mail, Camera, Shield, Loader2, Save } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, token, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    profile_picture: '',
    role: 'member'
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    setFormData({
      name: user.name || '',
      profile_picture: user.profile_picture || '',
      role: user.role || 'member'
    });
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(`${API}/auth/profile`, {
        name: formData.name,
        profile_picture: formData.profile_picture || null,
        role: formData.role
      }, { headers: getAuthHeader() });

      // Check if committee request was submitted
      if (formData.role === 'committee' && user.role === 'member') {
        toast.success('Committee membership request submitted for president approval!');
      } else {
        toast.success('Profile updated successfully!');
      }
      
      // Refresh page to update user context
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'president':
        return 'bg-purple-100 text-purple-800';
      case 'committee':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-stone-100 text-stone-800';
    }
  };

  if (!user) return null;

  return (
    <Layout>
      <div className="bg-secondary py-8 border-b border-stone-200">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h1 className="font-display text-3xl font-bold text-stone-900">Account Settings</h1>
          <p className="text-stone-600 mt-2">Manage your profile and membership</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid gap-8">
          {/* Profile Picture Section */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary" />
                Profile Picture
              </CardTitle>
              <CardDescription>Add a photo to personalize your account</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6">
                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-stone-200 flex-shrink-0">
                  {formData.profile_picture ? (
                    <img 
                      src={formData.profile_picture.startsWith('/') 
                        ? `${process.env.REACT_APP_BACKEND_URL}${formData.profile_picture}` 
                        : formData.profile_picture
                      } 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-full h-full bg-primary/10 flex items-center justify-center ${formData.profile_picture ? 'hidden' : ''}`}
                  >
                    <User className="w-10 h-10 text-primary" />
                  </div>
                </div>
                <div className="flex-1">
                  <ImageUploader
                    label="Upload or enter image URL"
                    value={formData.profile_picture}
                    onChange={(url) => setFormData({ ...formData, profile_picture: url })}
                    token={token}
                    uploadEndpoint="/upload/profile-picture"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info Section */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className="bg-stone-50"
                  data-testid="profile-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email Address</Label>
                <div className="flex items-center gap-2 p-3 bg-stone-100 rounded-md">
                  <Mail className="w-4 h-4 text-stone-500" />
                  <span className="text-stone-600">{user.email}</span>
                  <Badge variant="outline" className="ml-auto text-xs">Cannot be changed</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Membership Status Section */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Membership Status
              </CardTitle>
              <CardDescription>
                Change your membership level and access permissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-stone-600">Current Status:</span>
                <Badge className={getRoleBadgeColor(user.role)}>
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </Badge>
              </div>

              <RadioGroup 
                value={formData.role} 
                onValueChange={(value) => setFormData({ ...formData, role: value })}
                className="space-y-3"
              >
                <div className="flex items-start space-x-3 p-4 rounded-lg border border-stone-200 hover:border-primary/50 transition-colors">
                  <RadioGroupItem value="member" id="role-member" className="mt-1" data-testid="profile-role-member" />
                  <div className="flex-1">
                    <Label htmlFor="role-member" className="font-medium cursor-pointer">Member</Label>
                    <p className="text-sm text-stone-500 mt-1">
                      Register for events and view your registrations
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 p-4 rounded-lg border border-stone-200 hover:border-primary/50 transition-colors">
                  <RadioGroupItem value="committee" id="role-committee" className="mt-1" data-testid="profile-role-committee" />
                  <div className="flex-1">
                    <Label htmlFor="role-committee" className="font-medium cursor-pointer">Committee Member</Label>
                    <p className="text-sm text-stone-500 mt-1">
                      Create and manage events. Changes require president approval.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 p-4 rounded-lg border border-stone-200 hover:border-primary/50 transition-colors">
                  <RadioGroupItem value="president" id="role-president" className="mt-1" data-testid="profile-role-president" />
                  <div className="flex-1">
                    <Label htmlFor="role-president" className="font-medium cursor-pointer">President</Label>
                    <p className="text-sm text-stone-500 mt-1">
                      Full admin access. Approve changes and manage all aspects of the club.
                    </p>
                  </div>
                </div>
              </RadioGroup>

              {formData.role !== user.role && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    {formData.role === 'committee' && user.role === 'member' ? (
                      <><strong>Note:</strong> Committee membership requires president approval. Your request will be reviewed.</>
                    ) : formData.role === 'president' && user.role !== 'president' ? (
                      <><strong>Note:</strong> President role can only be assigned by an existing president.</>
                    ) : (
                      <><strong>Note:</strong> Changing your membership status will update your access permissions.</>
                    )}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-primary hover:bg-primary/90"
              data-testid="profile-save-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>

          {/* Account Info */}
          <Card className="border-stone-200 bg-stone-50">
            <CardContent className="p-6">
              <p className="text-sm text-stone-500">
                <strong>Account created:</strong>{' '}
                {new Date(user.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default ProfilePage;
